import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, TrendingUp, TrendingDown, Plus, Trash2, Sun, Moon, User,
  Calendar, Receipt, FileText, Briefcase, AlertCircle, Check, Loader2,
  RefreshCw, X, Wallet, CreditCard, ArrowUpCircle, ArrowDownCircle
} from 'lucide-react';
import API_BASE_URL from '../../../config/baseApi';

export default function Transactions() {
  const navigate = useNavigate();
  
  const [theme, setTheme] = useState('dark');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({
    total_income: 0,
    total_expenses: 0,
    balance: 0
  });
  
  const [notification, setNotification] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Expense form state
  const [expenseForm, setExpenseForm] = useState({
    bill_no: '',
    category: '',
    amount: '',
    handle_by: '',
    description: ''
  });
  
  // Income form state
  const [incomeForm, setIncomeForm] = useState({
    amount: '',
    description: ''
  });

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [transactionsRes, categoriesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/transactions`, {
          headers: { 'Accept': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/transactions/categories`, {
          headers: { 'Accept': 'application/json' }
        }),
        fetch(`${API_BASE_URL}/api/transactions/stats`, {
          headers: { 'Accept': 'application/json' }
        })
      ]);

      if (!transactionsRes.ok || !categoriesRes.ok || !statsRes.ok) {
        throw new Error('Failed to fetch data from server');
      }

      const transactionsData = await transactionsRes.json();
      const categoriesData = await categoriesRes.json();
      const statsData = await statsRes.json();

      if (transactionsData.success) setTransactions(transactionsData.data || []);
      if (categoriesData.success) setCategories(categoriesData.data || []);
      if (statsData.success) {
        setStats({
          total_income: Number(statsData.data.total_income || 0),
          total_expenses: Number(statsData.data.total_expenses || 0),
          balance: Number(statsData.data.balance || 0),
        });
      }


    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('Error loading data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setProcessing(true);

      const response = await fetch(`${API_BASE_URL}/api/transactions/expense`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(expenseForm),
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to add expense');
      }

      showNotification('Expense added successfully!', 'success');
      setExpenseForm({
        bill_no: '',
        category: '',
        amount: '',
        handle_by: '',
        description: ''
      });
      
      await fetchAllData();
    } catch (error) {
      console.error('Error adding expense:', error);
      showNotification('Error: ' + error.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setProcessing(true);

      const response = await fetch(`${API_BASE_URL}/api/transactions/income`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(incomeForm),
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to add income');
      }

      showNotification('Income added successfully!', 'success');
      setIncomeForm({
        amount: '',
        description: ''
      });
      
      await fetchAllData();
    } catch (error) {
      console.error('Error adding income:', error);
      showNotification('Error: ' + error.message, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/transactions/${deleteTarget}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to delete transaction');
      }

      showNotification('Transaction deleted successfully', 'success');
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      showNotification('Error: ' + error.message, 'error');
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-white/60">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900 transition-colors duration-300">
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl backdrop-blur-xl border ${
            notification.type === 'success' 
              ? 'bg-emerald-500/90 border-emerald-400 text-white' 
              : notification.type === 'error'
              ? 'bg-red-500/90 border-red-400 text-white'
              : 'bg-blue-500/90 border-blue-400 text-white'
          }`}>
            {notification.type === 'success' && <Check className="w-5 h-5" />}
            {notification.type === 'error' && <AlertCircle className="w-5 h-5" />}
            {notification.type === 'info' && <AlertCircle className="w-5 h-5" />}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-[1800px] mx-auto px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <ArrowUpCircle className="w-6 h-6 text-white" />
              </div>
              <TrendingUp className="w-8 h-8 text-white/40" />
            </div>
            <p className="text-white/80 text-sm mb-1">Total Income</p>
            <p className="text-white text-3xl font-bold">Rs {Number(stats.total_income || 0).toFixed(2)}</p>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-orange-600 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <ArrowDownCircle className="w-6 h-6 text-white" />
              </div>
              <TrendingDown className="w-8 h-8 text-white/40" />
            </div>
            <p className="text-white/80 text-sm mb-1">Total Expenses</p>
            <p className="text-white text-3xl font-bold">Rs {Number(stats.total_expenses || 0).toFixed(2)}</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <DollarSign className="w-8 h-8 text-white/40" />
            </div>
            <p className="text-white/80 text-sm mb-1">Balance</p>
            <p className="text-white text-3xl font-bold">Rs {Number(stats.balance || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Forms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Expense Form */}
          <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
            <h3 className="text-slate-900 dark:text-white text-xl font-semibold mb-6 flex items-center gap-2">
              <TrendingDown className="w-6 h-6 text-red-600 dark:text-red-400" />
              Add Expense
            </h3>
            
            <form onSubmit={handleExpenseSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Bill No</label>
                  <input
                    type="text"
                    value={expenseForm.bill_no}
                    onChange={(e) => setExpenseForm({...expenseForm, bill_no: e.target.value})}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-500/50"
                    required
                  />
                </div>

                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Category</label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm({...expenseForm, category: e.target.value})}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-500/50"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.cat_name}>{cat.cat_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-500/50"
                    required
                  />
                </div>

                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Handle By</label>
                  <input
                    type="text"
                    value={expenseForm.handle_by}
                    onChange={(e) => setExpenseForm({...expenseForm, handle_by: e.target.value})}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-500/50"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Description</label>
                <input
                  type="text"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-500/50"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={processing}
                className="w-full bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    <span>Add Expense</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Income Form */}
          <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
            <h3 className="text-slate-900 dark:text-white text-xl font-semibold mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              Add Income
            </h3>
            
            <form onSubmit={handleIncomeSubmit} className="space-y-4">
              <div>
                <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={incomeForm.amount}
                  onChange={(e) => setIncomeForm({...incomeForm, amount: e.target.value})}
                  className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500/50"
                  required
                />
              </div>

              <div>
                <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Description</label>
                <input
                  type="text"
                  value={incomeForm.description}
                  onChange={(e) => setIncomeForm({...incomeForm, description: e.target.value})}
                  className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500/50"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={processing}
                className="w-full bg-gradient-to-r from-green-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    <span>Add Income</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-slate-900 dark:text-white text-xl font-semibold flex items-center gap-2">
              <Receipt className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              All Transactions
            </h3>
            <span className="px-3 py-1 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 text-sm font-medium">
              {transactions.length} records
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100 dark:bg-white/5">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Bill No</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-white/60 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-white/5">
                {transactions.length > 0 ? (
                  transactions.map((transaction, index) => (
                    <tr key={transaction.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{index + 1}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium ${
                          transaction.status === 'Income'
                            ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'
                        }`}>
                          {transaction.status === 'Income' ? (
                            <ArrowUpCircle className="w-4 h-4" />
                          ) : (
                            <ArrowDownCircle className="w-4 h-4" />
                          )}
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">{transaction.bill_no || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-white/60">{transaction.ex_cat || '-'}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-white">
                        Rs {parseFloat(transaction.amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-white/60">
                        {new Date(transaction.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(transaction.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-500/30 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <Receipt className="w-12 h-12 text-slate-300 dark:text-white/20 mx-auto mb-3" />
                      <p className="text-slate-500 dark:text-white/40">No transactions found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-white/10 p-8 max-w-md w-full shadow-2xl animate-scale-in">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-slate-900 dark:text-white text-2xl font-bold mb-2">Are you sure?</h3>
              <p className="text-slate-600 dark:text-white/60 mb-6">
                This will permanently delete this transaction. This action cannot be undone!
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/20 text-slate-900 dark:text-white font-semibold py-3 px-6 rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
        
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}